import { Inject, Injectable } from '@angular/core';
import { from } from 'rxjs';
import { first, retryWhen, delay, take } from 'rxjs/operators';
import { CoreServiceConfig, CORE_SERVICES_CONFIG } from '../../core-services.config';
import { CoreServicesModule } from '../../core-services.module';
import { ISystem } from '../models';
import { MemoizationPromiseFnBuilderWithLocalStorage } from '../Memoization';

const fetchWeatherInfo = async (
  location: { lat: string; lng: string },
  lang = 'en',
  weatherConfig: {
    url: string,
    apiKeys: string[],
  }
) => {
  const WeatherApiKeys = weatherConfig.apiKeys;
  const WeatherApiKeyDefault = WeatherApiKeys[0];
  const WeatherUrl = weatherConfig.url;
  const params = {
    lat: location.lat,
    lon: location.lng,
    lang,
    units: 'metric',
    appid: WeatherApiKeyDefault,
  };
  const url = new URL(WeatherUrl);
  for (const [key, param] of Object.entries(params)) {
    url.searchParams.append(key, param);
  }
  const response = await from(fetch(url.toString())).pipe(
    retryWhen((errors) => errors.pipe(delay(1000), take(60))),
  ).toPromise();

  if (response.status === 200) {
    return await response.json();
  }
  return undefined;
};

@Injectable({
  providedIn: CoreServicesModule,
})
export class WeatherService {
  private weatherConfig?: {
    url: string,
    apiKeys: string[],
  };

  constructor(@Inject(CORE_SERVICES_CONFIG) config: CoreServiceConfig) {
    this.weatherConfig = config.weather;
  }

  private memoizedFetchWeatherInfo = MemoizationPromiseFnBuilderWithLocalStorage(
    (location: { lat: string; lng: string }, lang = 'en') =>
      fetchWeatherInfo(location, lang, this.weatherConfig!),
    60 * 60 * 1000
  );

  async getWeatherInfo (uiSystem: ISystem, lang = 'en') {
    if(!this.weatherConfig) {
      return;
    }
    const locationOnString = await uiSystem.getCoordinates().pipe(first()).toPromise();
    const location = {
      lat: locationOnString.split(',')[0]?.trim(),
      lng: locationOnString.split(',')[1]?.trim(),
    };
    return await this.memoizedFetchWeatherInfo(location, lang);
  }

  async getWeatherInfoByLocation (location: { lat: string; lng: string }, lang = 'en') {
    if(!this.weatherConfig) {
      return;
    }
    return await this.memoizedFetchWeatherInfo(location, lang);
  }
}
